import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchRoomTypes, fetchRatePlans, createRatePlan, updateRatePlan, deleteRatePlan, getCurrency,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — Phase A1: rate plans (room type x occupancy x meal plan).
// The reservation form resolves its nightly rate from these; rack_rate feeds
// charged-vs-rack variance.
const S = {
  en: {
    title: "Rate plans", subtitle: "Nightly rate per room type, occupancy and meal plan",
    back: "Back to rooms", type: "Room type", occ: "Occupancy", meal: "Meal plan",
    rate: "Rate", rack: "Rack rate", add: "Add plan", save: "Save", cancel: "Cancel",
    edit: "Edit", del: "Delete", del_q: "Remove this rate plan?",
    none: "No rate plans yet. Add one so bookings price themselves automatically.",
    need_type: "Add a room type first.", could_not_save: "Could not save.",
    single: "Single", double: "Double",
    ro: "Room only", bb: "Bed & breakfast", hb: "Half board", fb: "Full board",
  },
  am: {
    title: "የዋጋ ዕቅዶች", subtitle: "በክፍል ዓይነት፣ በመኝታና በምግብ ዕቅድ የሌሊት ዋጋ",
    back: "ወደ ክፍሎች ተመለስ", type: "የክፍል ዓይነት", occ: "መኝታ", meal: "የምግብ ዕቅድ",
    rate: "ዋጋ", rack: "መደበኛ ዋጋ", add: "ዕቅድ ጨምር", save: "አስቀምጥ", cancel: "ሰርዝ",
    edit: "አርትዕ", del: "አጥፋ", del_q: "ይህን የዋጋ ዕቅድ ማስወገድ?",
    none: "እስካሁን የዋጋ ዕቅዶች የሉም። ቦታ ማስያዣ ራሱን በራሱ እንዲያሰላ አንድ ይጨምሩ።",
    need_type: "መጀመሪያ የክፍል ዓይነት ይጨምሩ።", could_not_save: "ማስቀመጥ አልተቻለም።",
    single: "ነጠላ", double: "ድርብ",
    ro: "ክፍል ብቻ", bb: "ከቁርስ ጋር", hb: "ግማሽ ቦርድ", fb: "ሙሉ ቦርድ",
  },
  om: {
    title: "Karoora gatii", subtitle: "Gatii halkanii gosa kutaa, teessuma fi karoora nyaataatiin",
    back: "Gara kutaalee deebi'i", type: "Gosa kutaa", occ: "Teessuma", meal: "Karoora nyaataa",
    rate: "Gatii", rack: "Gatii sadarkaa", add: "Karoora dabali", save: "Kaa'i", cancel: "Haqi",
    edit: "Gulaali", del: "Haqi", del_q: "Karoora gatii kana haquu?",
    none: "Ammatti karoorri gatii hin jiru. Akka bookingiin ofumaan gatii baasu tokko dabali.",
    need_type: "Duraan gosa kutaa dabali.", could_not_save: "Kaa'uu hin dandeenye.",
    single: "Nama tokko", double: "Nama lama",
    ro: "Kutaa qofa", bb: "Ciree waliin", hb: "Boordii walakkaa", fb: "Boordii guutuu",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const OCCS = ["single", "double"];
const MEALS = ["ro", "bb", "hb", "fb"];
const empty = { room_type_id: "", occupancy: "single", meal_plan: "ro", amount: "", rack_rate: "" };

export default function RatePlansPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [types, setTypes] = useState([]);
  const [plans, setPlans] = useState(null);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState("");

  const load = () => fetchRatePlans().then(setPlans).catch(() => setPlans([]));
  useEffect(() => {
    fetchRoomTypes().then((ts) => {
      setTypes(ts || []);
      setForm((f) => ({ ...f, room_type_id: (ts && ts[0] && ts[0].id) || "" }));
    }).catch(() => setTypes([]));
    load();
  }, []);

  const typeName = (id) => {
    const tp = types.find((x) => x.id === id);
    return tp ? tp.name : "—";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.room_type_id) { setErr(t("need_type")); return; }
    const body = {
      room_type_id: Number(form.room_type_id),
      occupancy: form.occupancy,
      meal_plan: form.meal_plan,
      amount: form.amount === "" ? 0 : Number(form.amount),
      rack_rate: form.rack_rate === "" ? null : Number(form.rack_rate),
    };
    try {
      if (editId) await updateRatePlan(editId, body);
      else await createRatePlan(body);
      setForm({ ...empty, room_type_id: form.room_type_id });
      setEditId(null);
      load();
    } catch {
      setErr(t("could_not_save"));
    }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      room_type_id: p.room_type_id, occupancy: p.occupancy, meal_plan: p.meal_plan,
      amount: p.amount ?? "", rack_rate: p.rack_rate ?? "",
    });
  };

  const remove = async (p) => {
    if (!window.confirm(t("del_q"))) return;
    setPlans((ps) => ps.filter((x) => x.id !== p.id));
    try { await deleteRatePlan(p.id); } catch { load(); }
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
  const sel = { ...inp, background: "#fff" };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/rooms" style={{ fontSize: 13, textDecoration: "none" }}>← {t("back")}</Link>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <form onSubmit={submit} style={{ ...CARD, padding: 14, marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <select value={form.room_type_id} onChange={(e) => setForm({ ...form, room_type_id: e.target.value })} style={{ ...sel, minWidth: 140 }}>
          {types.length === 0 && <option value="">—</option>}
          {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
        </select>
        <select value={form.occupancy} onChange={(e) => setForm({ ...form, occupancy: e.target.value })} style={sel}>
          {OCCS.map((o) => <option key={o} value={o}>{t(o)}</option>)}
        </select>
        <select value={form.meal_plan} onChange={(e) => setForm({ ...form, meal_plan: e.target.value })} style={sel}>
          {MEALS.map((m) => <option key={m} value={m}>{t(m)}</option>)}
        </select>
        <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={t("rate")} type="number" min="0" style={{ ...inp, width: 110 }} />
        <input value={form.rack_rate} onChange={(e) => setForm({ ...form, rack_rate: e.target.value })} placeholder={t("rack")} type="number" min="0" style={{ ...inp, width: 110 }} />
        <button className="btn primary small" type="submit">{editId ? t("save") : t("add")}</button>
        {editId && <button className="btn secondary small" type="button" onClick={() => { setEditId(null); setForm({ ...empty, room_type_id: form.room_type_id }); }}>{t("cancel")}</button>}
        {err && <span style={{ color: "#A32D2D", fontSize: 13, width: "100%" }}>{err}</span>}
      </form>

      {plans == null ? (
        <p className="muted" style={{ marginTop: 14 }}>…</p>
      ) : plans.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 14, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {plans.map((p) => (
            <div key={p.id} style={{ ...CARD, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {p.room_type_name || typeName(p.room_type_id)} · {t(p.occupancy)} · {t(p.meal_plan)}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {formatPrice(p.amount || 0, cur)}
                  {p.rack_rate != null && p.rack_rate !== p.amount && <> · {t("rack")} {formatPrice(p.rack_rate, cur)}</>}
                </div>
              </div>
              <button className="btn secondary small" onClick={() => startEdit(p)}>{t("edit")}</button>
              <button onClick={() => remove(p)} style={{ fontSize: 13, background: "none", border: "none", color: "#A32D2D", cursor: "pointer" }}>{t("del")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
